/* eslint-disable @typescript-eslint/no-explicit-any */
import {Injectable} from '@angular/core';
import {LookupService} from '../../common/service/lookup.service';
import {MapToolsService} from './map-tools.service';
import {SpatialUtilService} from './spatial-util.service';
import {NgEventBus} from 'ng-event-bus';
import {SelectionMethodLookup} from '../../common/models/selection-method.model';
import {FeatureClassSelectionLookup} from '../../common/models/feature-class-selection.model';

@Injectable({
    providedIn: 'root'
})
export class GraphicSelectionService {
    title = 'GraphicSelectionService';

    selectionMethod: SelectionMethodLookup;

    featureClass: FeatureClassSelectionLookup;

    selectionToolbar = null;

    featureLayers = [];

    onDrawEndEvent = null;

    onDrawCompleteHandler = null;

    mapOnBeforeUnload = null;

    deactivatedFeatureLayers: any[] = null;

    constructor(
        private eventBus: NgEventBus,
        private lookupService: LookupService,
        private mapToolsService: MapToolsService,
        private spatialUtilService: SpatialUtilService
    ) {}

    activate(onDrawCompleteHandler?: any) {
        console.debug(this.title, 'activate');

        const selectionMethodName = this.selectionMethod?.name;
        if (!selectionMethodName) {
            return;
        }

        if (onDrawCompleteHandler) {
            this.onDrawCompleteHandler = onDrawCompleteHandler;
        }

        const map = this.spatialUtilService.getMap();
        if (!map) {
            return;
        }

        this.mapOnBeforeUnload =
            this.mapOnBeforeUnload ||
            map.on('before-unload', () => {
                console.info(`${this.title} - map.on("before-unload")`);

                this.mapOnBeforeUnload.remove();
                this.mapOnBeforeUnload = null;

                if (this.mapToolsService.isActive('MAP_SELECTION_CLICK')) {
                    this.mapToolsService.deactivate('MAP_SELECTION_CLICK');
                }

                if (this.mapToolsService.isActive('MAP_SELECTION_DRAG')) {
                    this.mapToolsService.deactivate('MAP_SELECTION_DRAG');
                }
            });

        this.mapToolsService
            .activate(
                'MAP_SELECTION_CLICK',
                (restore: any) => {
                    console.info(`${this.title} - MAP_SELECTION_CLICK - restore: ${restore}`);

                    if (this.deactivatedFeatureLayers && this.deactivatedFeatureLayers.length) {
                        console.info(`${this.title} - MAP_SELECTION_CLICK - deactivatedFeatureLayers length: `, this.deactivatedFeatureLayers.length);
                        this.featureLayers = this.deactivatedFeatureLayers;
                        this.deactivatedFeatureLayers = null;
                    }

                    this.eventBus.cast('resetMapClickHandler');

                    switch (selectionMethodName) {
                        case 'CIRCLE':
                        case 'ELLIPSE':
                        case 'FREEHAND_POLYGON':
                        case 'RECTANGLE':
                            map.setMapCursor(this.spatialUtilService.getCrossCursorUrl());
                            break;
                        case 'LINE':
                        case 'FREEHAND_POLYLINE':
                            map.setMapCursor(this.spatialUtilService.getPencilCursorUrl());
                            break;
                        case 'POINT':
                        default:
                            map.setMapCursor(this.spatialUtilService.getDefaultCursorUrl());
                            break;
                    }
                },
                () => {
                    if (!this.mapToolsService.isActive('MAP_SELECTION_DRAG')) {
                        if (this.featureLayers.length) {
                            this.deactivatedFeatureLayers = this.featureLayers;
                            this.featureLayers = [];
                        }

                        const map = this.spatialUtilService.getMap();
                        if (map) {
                            map.setMapCursor(this.spatialUtilService.getDefaultCursorUrl());
                        }
                    }

                    this.eventBus.cast('deleteMapClickHandler');
                }
            )
            .subscribe();

        this.mapToolsService
            .activate(
                'MAP_SELECTION_DRAG',
                (restore: any) => {
                    console.info(`${this.title} - MAP_SELECTION_DRAG - restore: ${restore}`);

                    if (this.deactivatedFeatureLayers && this.deactivatedFeatureLayers.length) {
                        console.info(`${this.title} - MAP_SELECTION_CLICK - deactivatedFeatureLayers length: `, this.deactivatedFeatureLayers.length);
                        this.featureLayers = this.deactivatedFeatureLayers;
                        this.deactivatedFeatureLayers = null;
                    }

                    if (selectionMethodName !== 'POINT') {
                        this.selectionToolbar = new (window as any).esri.toolbars.Draw(map, {tolerance: 1, showTooltips: false});
                        this.selectionToolbar.activate((window as any).esri.toolbars.Draw[selectionMethodName]);

                        this.onDrawEndEvent = this.selectionToolbar.on('draw-complete', (evt: any) => this.onDrawCompleteHandler(evt));
                    }

                    switch (selectionMethodName) {
                        case 'CIRCLE':
                        case 'ELLIPSE':
                        case 'FREEHAND_POLYGON':
                        case 'RECTANGLE':
                            map.setMapCursor(this.spatialUtilService.getCrossCursorUrl());
                            break;
                        case 'LINE':
                        case 'FREEHAND_POLYLINE':
                            map.setMapCursor(this.spatialUtilService.getPencilCursorUrl());
                            break;
                        case 'POINT':
                        default:
                            map.setMapCursor(this.spatialUtilService.getDefaultCursorUrl());
                            break;
                    }
                },
                () => {
                    const map = this.spatialUtilService.getMap();
                    if (!map) {
                        return;
                    }

                    if (!this.mapToolsService.isActive('MAP_SELECTION_CLICK')) {
                        if (this.featureLayers.length) {
                            this.deactivatedFeatureLayers = this.featureLayers;
                            this.featureLayers = [];
                        }

                        map.setMapCursor(this.spatialUtilService.getDefaultCursorUrl());
                    }

                    if (this.onDrawEndEvent) {
                        this.onDrawEndEvent.remove();
                    }

                    if (this.selectionToolbar) {
                        this.selectionToolbar.deactivate();
                    }
                }
            )
            .subscribe();
    }

    getSelectionMethod() {
        return this.selectionMethod;
    }

    setSelectionMethod(selectionMethod: SelectionMethodLookup) {
        const oldSelectionMethod = this.selectionMethod;
        this.selectionMethod = selectionMethod;

        if (!oldSelectionMethod || oldSelectionMethod.id !== selectionMethod.id) {
            this.activate();
        }
    }

    getFeatureSelection() {
        return this.featureClass;
    }

    setFeatureClass(featureClass: any) {
        this.featureClass = featureClass;
    }

    featureClassSelectionChanged() {
        console.debug(this.title, 'featureClassSelectionChanged');

        this.featureLayers = [];

        const featureClasses = this.featureClass.featureClasses.split(',');
        for (let i = 0; i < featureClasses.length; i++) {
            this.featureLayers.push(this.spatialUtilService.getMapFeatureLayer(featureClasses[i]));
        }
    }

    applyFeaturesFilter(featureClass: string, features: any[]) {
        console.debug(`${this.title} - applyFeaturesFilter`, `featureClass: ${featureClass}, features:`, features);

        const featureClasses = this.featureClass.featureClasses.split(',').map(fc => fc.toLowerCase());
        if (!featureClasses.includes(featureClass)) {
            return [];
        }

        let filteredFeatures = features;
        const businessClass = this.featureClass.businessClasses;
        const annotationFeatureType = this.featureClass.annotationFeatureTypes;

        if (businessClass) {
            switch (featureClass) {
                case 'line':
                case 'point':
                case 'polygon':
                    {
                        let lookupValues = [];

                        if (featureClass === 'line') {
                            lookupValues = this.lookupService.getLookups('lineType');
                        } else if (featureClass === 'point') {
                            lookupValues = this.lookupService.getLookups('pointType');
                        } else if (featureClass === 'polygon') {
                            lookupValues = this.lookupService.getLookups('legalParcelType').filter(l => l.id !== 97);
                        }

                        const businessClassList = businessClass.split(',');
                        const businessClassLineTypes = lookupValues.filter(l => businessClassList.includes(l.businessClass)).map(l => l.id);

                        filteredFeatures = features.filter(f => f.attributes && businessClassLineTypes.includes(f.attributes.type));
                    }
                    break;
                case 'polyline':
                    filteredFeatures = [];
                    break;
            }
        }

        if (annotationFeatureType) {
            if (annotationFeatureType === 'controlPoint' || annotationFeatureType === 'point') {
                let lookupValues = [];

                if (annotationFeatureType === 'controlPoint') {
                    lookupValues = this.lookupService.getLookups('controlPointType');
                } else {
                    lookupValues = this.lookupService.getLookups('pointType');
                }

                filteredFeatures = features.filter(feature => {
                    return feature.attributes?.featuretype === 'point' && lookupValues.some(item => feature.attributes.featuresubtype === item.id);
                });
            } else {
                const annotationFeatureTypeList = annotationFeatureType.split(',');
                filteredFeatures = features.filter(feature => annotationFeatureTypeList.includes(feature.attributes?.featuretype));
            }
        }

        if (featureClass === 'polygon') {
            filteredFeatures = features.filter(g => {
                const attrType = parseInt(g.attributes?.type);
                return attrType && !isNaN(attrType) && attrType !== 97;
            });
        }

        return filteredFeatures;
    }
}
